import { CogLayerConfig, PMTilesLayerConfig, SpatialDataset } from "../types";
import { SPATIAL_ACTIVE_LAYER_ID } from "../constants/spatialDataset";

/**
 * Convert a completed SpatialDataset into the appropriate MapLayerConfig.
 *
 * Vector geometry type: derived from display_style paint keys as a stop-gap
 * until the backend emits geometry_type (SDW-19). Once geometry_type lands,
 * replace inferVectorLayerType with: geomToLayerType[ds.geometry_type ?? "polygon"]
 */
function inferVectorLayerType(displayStyle: Record<string, any>): "fill" | "line" | "circle" {
  const keys = Object.keys(displayStyle ?? {});
  if (keys.some((k) => k.startsWith("line-"))) return "line";
  if (keys.some((k) => k.startsWith("circle-"))) return "circle";
  return "fill";
}

const DEFAULT_PAINT: Record<string, Record<string, any>> = {
  fill: { "fill-color": "#3b82f6", "fill-opacity": 0.5 },
  line: { "line-color": "#3b82f6", "line-width": 2 },
  circle: { "circle-color": "#3b82f6", "circle-radius": 5 }
};

export function toLayerConfig(ds: SpatialDataset): CogLayerConfig | PMTilesLayerConfig | null {
  if (!ds.display_url || !ds.display_type) return null;

  const bounds = ds.bbox
    ? ([
        [ds.bbox[0], ds.bbox[1]],
        [ds.bbox[2], ds.bbox[3]]
      ] as [[number, number], [number, number]])
    : undefined;

  if (ds.display_type === "cog") {
    return {
      id: SPATIAL_ACTIVE_LAYER_ID,
      name: ds.name,
      type: "cog",
      url: ds.display_url,
      initialVisibility: false,
      bounds,
      options: {
        opacity: ds.display_style?.["raster-opacity"] ?? 1,
        minZoom: 0,
        // maxZoom from backend SDW-13 — treat missing options as no clamp, not a crash
        maxZoom: 24
      }
    } as CogLayerConfig;
  }

  if (ds.display_type === "pmtiles" && ds.dataset_type === "vector") {
    const hasStyle = Object.keys(ds.display_style ?? {}).length > 0;
    const layerType = ds.geometry_type
      ? (({ point: "circle", line: "line", polygon: "fill" } as const)[ds.geometry_type] ?? "fill")
      : inferVectorLayerType(ds.display_style ?? {});
    const paint = hasStyle ? ds.display_style : DEFAULT_PAINT[layerType];

    return {
      id: SPATIAL_ACTIVE_LAYER_ID,
      name: ds.name,
      type: "pmtiles",
      url: ds.display_url,
      layerFormat: "vector",
      // tippecanoe output layer is always the literal string "layer"
      sourceLayer: "layer",
      initialVisibility: false,
      bounds,
      style: { type: layerType, paint },
      options: { minZoom: 0, maxZoom: 24 }
    } as PMTilesLayerConfig;
  }

  if (ds.display_type === "pmtiles" && ds.dataset_type === "raster") {
    return {
      id: SPATIAL_ACTIVE_LAYER_ID,
      name: ds.name,
      type: "pmtiles",
      url: ds.display_url,
      layerFormat: "raster",
      initialVisibility: false,
      bounds,
      options: {
        opacity: ds.display_style?.["raster-opacity"] ?? 1,
        tileSize: 256,
        minZoom: 0,
        maxZoom: 24
      }
    } as PMTilesLayerConfig;
  }

  return null;
}

/**
 * Layers are registered on demand when the user selects a dataset in SideBar.tsx.
 * This hook is a no-op so existing call sites in MapComponent don't need to change.
 */
export const useSpatialDatasetMapLayers = () => {};
